import React, { useState } from 'react';
import axios from '../api/axios';
import styles from './FeedbackForm.module.css';
import { MessageSquare, X, Send } from 'lucide-react';

interface FeedbackFormProps {
  onClose: () => void;
  onSuccess: () => void;
}

const PAGE_OPTIONS = [
  'Dashboard',
  'Knowledge Base',
  'Events',
  'Missions',
  'Profile',
  'Departments',
  'Medical SOS',
  'After Action Reports',
  'Video Library',
  'Entire Website',
  'Other'
];

const CATEGORY_OPTIONS = [
  'Bug',
  'Feature Request',
  'UI/UX',
  'Performance',
  'Other'
];

const FeedbackForm: React.FC<FeedbackFormProps> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    pageScope: '',
    category: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.pageScope || !formData.category || !formData.message.trim()) {
      setError('All fields are required.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await axios.post('/feedback', formData);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Failed to submit feedback:', err);
      console.log('Token in localStorage:', localStorage.getItem('token') ? 'exists' : 'missing');
      console.log('Error response:', err.response);
      
      // Handle specific error cases
      if (err.response?.status === 401) {
        setError('You must be logged in to submit feedback. Please log in and try again.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to submit feedback.');
      } else {
        setError(err.response?.data?.message || 'Failed to submit feedback. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <MessageSquare size={24} />
            <h2>Submit Feedback</h2>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.formGroup}>
            <label htmlFor="pageScope">Page or Section *</label>
            <select
              id="pageScope"
              name="pageScope"
              value={formData.pageScope}
              onChange={handleChange}
              required
              className={styles.select}
            >
              <option value="">-- Select Page --</option>
              {PAGE_OPTIONS.map(page => (
                <option key={page} value={page}>{page}</option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="category">Category *</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              required
              className={styles.select}
            >
              <option value="">-- Select Category --</option>
              {CATEGORY_OPTIONS.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="message">Your Feedback *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              placeholder="Please describe your feedback in detail..."
              className={styles.textarea}
            />
            <div className={styles.charCount}>
              {formData.message.length} characters
            </div>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelBtn}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>Submitting...</>
              ) : (
                <>
                  <Send size={18} />
                  Submit Feedback
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;
